"use strict";

const BrowserDetect = require("@gluenetworks/srvc_core_browser-detect");
const _ = require("lodash");

class InputPasswordControlRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("input-password-control");
        this._textField = document.createElement("input");
        this._textField.setAttribute("type", "password");
        if (this._model.autocompleteHint) {
            this._textField.setAttribute("autocomplete", this._model.autocompleteHint);
        }
        this._errorSpan = document.createElement("span");
        this._errorSpan.classList.add("error");
        if (this._model.disablePasswordMgt) {
            this._fakeTextField = document.createElement("input");
            this._fakeTextField.setAttribute("type", "password");
            this._fakeTextField.classList.add("hidden");
            this._fakeTextField.value = "Password not available";
            this._rootElem.appendChild(this._fakeTextField);
        }
        this._rootElem.appendChild(this._textField);
        this._rootElem.appendChild(this._errorSpan);
        this._rootElem.component = this._model;
        this._isEdge = BrowserDetect.browser === "Edge";

        this._registerListeners();
        this.render();
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _shutdown() {
        let prom = new Promise((resolve /*,reject*/) => {
            this._unregisterListeners();
            this._model = undefined;
            this._rootElem.remove();
            this._textField = undefined;
            this._rootElem = undefined;
            this._isEdge = undefined;
            resolve();
        });
        return prom;
    }

    _registerListeners() {
        this._model.on("Disabled", (this._onDisabled = this._onDisabled.bind(this)));
        this._model.on("Value", (this._onValue = this._onValue.bind(this)));
        this._model.on("Visible", (this._onVisible = this._onVisible.bind(this)));
        this._model.on("Invalid", (this._onInvalid = this._onInvalid.bind(this)));
        this._model.on("Placeholder", (this._onPlaceholder = this._onPlaceholder.bind(this)));
        this._model.on("DarkTheme", (this._onDarkTheme = this._onDarkTheme.bind(this)));
        this._model.on("Theme", (this._onTheme = this._onTheme.bind(this)));
        this._model.on("Focus", (this._onFocus = this._onFocus.bind(this)));
        this._model.on("Blur", (this._onBlur = this._onBlur.bind(this)));
        this._model.on(
            "AutocompleteHint",
            (this._onAutocompleteHint = this._onAutocompleteHint.bind(this))
        );
        this._model.on("SetAttribute", (this._onSetAttribute = this._onSetAttribute.bind(this)));
        this._model.on("Required", (this._onRequired = this._onRequired.bind(this)));
        this._textField.addEventListener(
            "input",
            (this._onInputEvent = this._onInputEvent.bind(this))
        );
        this._textField.addEventListener(
            "focus",
            (this._onFocusEvent = this._onFocusEvent.bind(this))
        );
        this._textField.addEventListener(
            "blur",
            (this._onBlurEvent = this._onBlurEvent.bind(this))
        );
        this._textField.addEventListener(
            "keydown",
            (this._onKeyDownEvent = this._onKeyDownEvent.bind(this))
        );
        this._textField.addEventListener(
            "keypress",
            (this._onKeyPressEvent = this._onKeyPressEvent.bind(this))
        );
    }

    _unregisterListeners() {
        this._model.removeListener("Disabled", this._onDisabled);
        this._model.removeListener("Value", this._onValue);
        this._model.removeListener("Visible", this._onVisible);
        this._model.removeListener("Invalid", this._onInvalid);
        this._model.removeListener("Placeholder", this._onPlaceholder);
        this._model.removeListener("DarkTheme", this._onDarkTheme);
        this._model.removeListener("Theme", this._onTheme);
        this._model.removeListener("Focus", this._onFocus);
        this._model.removeListener("Blur", this._onBlur);
        this._model.removeListener("AutocompleteHint", this._onAutocompleteHint);
        this._model.removeListener("SetAttribute", this._onSetAttribute);
        this._model.removeListener("Required", this._onRequired);
        this._textField.removeEventListener("input", this._onInputEvent);
        this._textField.removeEventListener("focus", this._onFocusEvent);
        this._textField.removeEventListener("blur", this._onBlurEvent);
        this._textField.removeEventListener("keydown", this._onKeyDownEvent);
        this._textField.removeEventListener("keypress", this._onKeyPressEvent);
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        let prom = new Promise((resolve /*,reject*/) => {
            let isInvalid = this._model.invalid;
            this._setDisabled(this._model.disabled);
            this._setValue(this._model.value);
            this._setVisible(this._model.visible);
            this._setRequired(this._model.required);
            this._setInvalid(isInvalid || this._model.invalid);
            this._setPlaceholder(this._model.placeholder);
            this._setDarkTheme(this._model.darkTheme);
            this._setTheme(this._model.theme);
            resolve();
        });
        return prom;
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /**
     * Returns validation messages based on the validity of text value on this control
     * @returns {Array}
     */
    getViolations() {
        return this._textField.validity.valueMissing ? ["Please enter password"] : [];
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START HANDLER METHODS                   */
    /*******************************************/

    /**
     * Fired when the disabled state has been updated in the model
     * @param newVal {Boolean}
     * @private
     */
    _onDisabled(newVal) {
        this._setDisabled(newVal);
    }

    /**
     * Fired when the invalid state has been updated in the model
     * @param newVal {Boolean}
     * @private
     */
    _onInvalid(newVal) {
        this._setInvalid(newVal);
    }

    /**
     * Fired when the value has been updated in the model
     * @param newVal {String}
     * @param oldVal {String}
     * @private
     */
    _onValue(newVal /*, oldVal*/) {
        this._setValue(newVal);
    }

    /**
     * Fired when the visible state has been updated in the model
     * @param newVal {Boolean}
     * @private
     */
    _onVisible(newVal) {
        this._setVisible(newVal);
    }

    /**
     * Fired when the placeholder has been updated in the model
     * @param newVal {String}
     * @private
     */
    _onPlaceholder(newVal) {
        this._setPlaceholder(newVal);
    }

    /**
     * Fired when the dearkTheme has been updated in the model
     * @param newVal {String}
     * @private
     */
    _onDarkTheme(newVal) {
        this._setDarkTheme(newVal);
    }

    /**
     * Fired when the theme has been updated in the model
     * @param newVal {String}
     * @private
     */
    _onTheme(newVal, oldVal) {
        this._setTheme(newVal, oldVal);
    }

    /**
     * Fired when focus has been called in the model
     * @private
     */
    _onFocus() {
        this._setFocus(true);
    }

    /**
     * Fired when blur has been called in the model
     * @private
     */
    _onBlur() {
        this._setFocus(false);
    }

    /**
     * Fired when setAutocompleteHint is called on the model
     * @param newVal
     * @private
     */
    _onAutocompleteHint(newVal) {
        this._setAutocompleteHint(newVal);
    }

    /**
     * Fired when setAttribute has been called in the model
     * @param attrName {String} - The name of the HTML attribute to be set on the native control
     * @param attrValue {String|Undefined} - The value of the HTML attribute to be set on the native control
     * @private
     */
    _onSetAttribute(attrName, attrValue) {
        this._setAttribute(attrName, attrValue);
    }

    /**
     * Update the value in the model based on user input
     * @param event {Event}
     * @private
     */
    _onInputEvent(event) {
        let oldVal = _.cloneDeep(this._model.value);
        let newVal = this._textField.value;
        this._model.setValue(this._textField.value, true);
        this._model._triggerUserValue(newVal, oldVal, event);
        this._setInvalid(!this._textField.validity.valid);
    }

    /**
     * Pass the focus event up to the model
     * @param event {Event}
     * @private
     */
    _onFocusEvent(event) {
        if (this._model.disablePasswordMgt) {
            this._textField.readOnly = false;
        }
        this._model._triggerUserFocus(event);
    }

    /**
     * Pass the blur event up to the model
     * @param event {Event}
     * @private
     */
    _onBlurEvent(event) {
        if (this._model.disablePasswordMgt) {
            this._textField.readOnly = true;
        }
        this._model._triggerUserBlur(event);
    }

    /**
     * Pass the key down event to the model
     * @param event {Event}
     * @private
     */
    _onKeyDownEvent(event) {
        this._model._triggerUserKeyDown(event);
    }

    /**
     * Toggle the caps lock display based on characters typed by the user
     * @param event {Event}
     * @private
     */
    _onKeyPressEvent(event) {
        let charCode = event.charCode || event.keyCode,
            shiftOn = event.shiftKey;
        // If in the uppercase character range and shift is NOT on, or
        // if in the lowercase character range and shift is on
        if (
            (charCode >= 65 && charCode <= 90 && !shiftOn) ||
            (charCode >= 97 && charCode <= 122 && shiftOn)
        ) {
            this._setCapsLock(true);
        } else {
            this._setCapsLock(false);
        }
    }

    _onRequired(/**event*/) {
        this._setRequired(this._model.required);
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/

    /**
     * Toggle the disabled state
     * @param newVal {Boolean}
     * @private
     */
    _setDisabled(newVal) {
        if (newVal === true) {
            this._textField.disabled = true;
        } else {
            this._textField.disabled = false;
        }
    }

    /**
     * Toggle the invalid state
     * @param newVal {Boolean}
     * @private
     */
    _setInvalid(newVal) {
        let oldVal = this._model.invalid;
        if (newVal === true) {
            this._rootElem.classList.add("invalid");
            let violations = this.getViolations();
            this._errorSpan.textContent = violations.join("\n");
        } else {
            this._rootElem.classList.remove("invalid");
            this._errorSpan.textContent = "";
        }
        if (oldVal !== newVal) {
            this._model._invalid = newVal;
            this._model._triggerInvalidStatusEvent(newVal, oldVal);
            return;
        }
    }

    /**
     * Put the placeholder text in place and move the cursor to the front of the selection if placeholder text
     * is displaying.
     * @param newVal {String}
     * @private
     */
    _setPlaceholder(newVal) {
        if (newVal !== undefined && newVal !== null && newVal !== "") {
            this._textField.placeholder = newVal;
        } else {
            this._textField.placeholder = "";
        }
    }

    /**
     * Toggle the display text value of the button
     * @param newVal {String}
     * @private
     */
    _setValue(newVal) {
        if (newVal !== this._textField.value) {
            if (newVal !== undefined && newVal !== null && newVal !== "") {
                this._textField.value = newVal;
            } else {
                this._textField.value = "";
            }
        }
    }

    /**
     * Toggle the visible state
     * @param newVal {Boolean}
     * @private
     */
    _setVisible(newVal) {
        if (newVal === true) {
            this._rootElem.classList.remove("hidden");
        } else {
            this._rootElem.classList.add("hidden");
        }
    }

    /**
     * Toggle or set the dark theme
     * @param newVal {Boolean}
     * @private
     */
    _setDarkTheme(newVal) {
        if (newVal === true) {
            this._rootElem.classList.add("dark");
        } else {
            this._rootElem.classList.remove("dark");
        }
    }

    /**
     * Set the theme name
     * @param newVal {String}
     * @private
     */
    _setTheme(newVal, oldVal) {
        if (oldVal) {
            this._rootElem.classList.remove(oldVal);
        }
        if (newVal) {
            this._rootElem.classList.add(newVal);
        }
    }

    /**
     * Set or remove focus on the textfield
     * @param newVal {Boolean}
     * @private
     */
    _setFocus(newVal) {
        if (newVal === true) {
            this._textField.focus();
        } else {
            this._textField.blur();
        }
    }

    /**
     * If Edge, toggle the caps lock display based on the parameter
     * @param newVal {Boolean}
     * @private
     */
    _setCapsLock(newVal) {
        if (this._isEdge) {
            if (newVal === true) {
                this._textField.classList.add("caps-lock");
            } else {
                this._textField.classList.remove("caps-lock");
            }
        }
    }

    /**
     * Set autocomplete on the textfield
     * @param newVal {string}
     * @private
     */
    _setAutocompleteHint(newVal) {
        this._setAttribute("autocomplete", newVal);
    }

    /**
     * Set an attribute on the native control
     * @param attrName {String} - The name of the HTML attribute to be set on the native control
     * @param attrValue {String|Undefined} - The value of the HTML attribute to be set on the native control
     * @private
     */
    _setAttribute(attrName, attrValue) {
        if (!_.isUndefined(attrValue)) {
            this._textField.setAttribute(attrName, attrValue);
        } else {
            this._textField.removeAttribute(attrName);
        }
    }

    /**
     * Add / remove required attribute
     * @param isRequired {boolean} - new value of required attribute
     */
    _setRequired(isRequired) {
        if (isRequired === true) {
            this._textField.setAttribute("required", "");
        } else {
            this._textField.removeAttribute("required");
        }
        this._setInvalid(!this._textField.validity.valid);
    }

    /*******************************************/
    /*  END PRIVATE API METHODS                */
    /*******************************************/
}

module.exports = InputPasswordControlRenderer;
